// GA4 for the public SPA. The measurement id is public by design. When it is
// unset (local dev, CI, a deploy before the GitHub variable is set) every
// function in this module returns without touching the network or the DOM.

const GA_ID = (import.meta.env.VITE_GA4_MEASUREMENT_ID as string | undefined)?.trim() || undefined;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

let loaded = false;
let viewPath = safePathname();

function safePathname(): string {
  try {
    return location.pathname || '/';
  } catch {
    return '/';
  }
}

/** Remember the SPA view so a later consent grant records the view on screen. */
export function noteViewPath(path: string): void {
  viewPath = path;
}

export function currentViewPath(): string {
  return viewPath;
}

/** List is the document path. Map is the same path with `?view=map`. */
export function resultsViewPath(view: 'list' | 'map'): string {
  const base = safePathname();
  return view === 'map' ? `${base}?view=map` : base;
}

/** Call only after consent (or immediately if running Consent Mode). */
export function initAnalytics(): void {
  if (loaded || !GA_ID) return; // no-op if unconfigured
  loaded = true;

  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // gtag.js reads the Arguments object, matching the official snippet.
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, { send_page_view: false }); // SPA: manual page_view
}

/** Fire on each SPA route/view change. */
export function trackPageView(path: string): void {
  if (!loaded || !GA_ID) return;
  window.gtag('event', 'page_view', { page_path: path, page_location: location.href });
}

/** Outbound click on an event's original-page link. */
export function trackOutboundClick(opts: {
  eventId: string;
  eventTitle: string;
  url: string;
}): void {
  if (!loaded || !GA_ID) return;
  window.gtag('event', 'outbound_click', {
    event_id: opts.eventId,
    event_title: opts.eventTitle,
    link_url: opts.url,
    transport_type: 'beacon', // survives navigation away
  });
}

/** Filter change (category, date, distance, free-food toggle, etc.). */
export function trackFilterChange(filterName: string, filterValue: string): void {
  if (!loaded || !GA_ID) return;
  window.gtag('event', 'filter_change', { filter_name: filterName, filter_value: filterValue });
}

/** Detail opened from a card or a map pin. */
export function trackEventDetailOpen(opts: { eventId: string; source: 'card' | 'map' }): void {
  if (!loaded || !GA_ID) return;
  window.gtag('event', 'event_detail_open', {
    event_id: opts.eventId,
    source: opts.source,
  });
}

/** Explicit GPS ask. `unavailable` is not a grant or a denial, so it is not sent. */
export function trackUseMyLocation(outcome: 'granted' | 'denied'): void {
  if (!loaded || !GA_ID) return;
  window.gtag('event', 'use_my_location', { outcome });
}

/**
 * A place search finished. The text the visitor typed stays on the device —
 * only whether the lookup resolved is sent.
 */
export function trackLocationSearch(outcome: 'resolved' | 'unresolved'): void {
  if (!loaded || !GA_ID) return;
  window.gtag('event', 'location_search', { outcome });
}

/** Empty-state "Widen to N mi" action. */
export function trackWidenRadius(miles: number): void {
  if (!loaded || !GA_ID) return;
  window.gtag('event', 'widen_radius', { radius_miles: miles });
}
